import React from 'react';
import { useDispatch } from 'react-redux';

import { Button } from '@travelnest/merlin.button';
import { Modal } from '@travelnest/merlin.modal';
import { Select } from '@travelnest/merlin.select';
import { Spacer } from '@travelnest/merlin.spacer';
import TextArea from '@travelnest/merlin.text-area';
import theme from '@travelnest/merlin.theme';
import api from '../../../../../../api';
import {
	IEventNote,
	IPendingEventNote,
} from '../../../../../../data/entities/event/event-note';
import { FormInput, FormLabel } from '../../../../../../view/components/form';
import { Panel } from '../../../../../components/panel/Panel';
import { Paragraph } from '../../../../../components/paragraph/Paragraph';

interface ICancellationRequestModalProps {
	customerId: string;
	propertyId: number | string;
	eventId: string;
	onRequestClose: () => any;
	submitEventNotes: (
		propertyId: string,
		eventId: string,
		eventNotes: IPendingEventNote[]
	) => any;
}

export const CancellationRequestModal: React.FC<
	ICancellationRequestModalProps
> = ({ customerId, propertyId, eventId, onRequestClose, submitEventNotes }) => {
	const [reason, setReason] = React.useState('');
	const [instigator, setInstigator] = React.useState('');
	const [additionalInfo, setAdditionalInfo] = React.useState('');
	const [submitting, setSubmitting] = React.useState(false);
	const [errorSubmitting, setErrorSubmitting] = React.useState('');
	const dispatch = useDispatch();
	const [bypassChecked, setBypassChecked] = React.useState(false);

	const onSave = async () => {
		setSubmitting(true);
		try {
			const cancellationRequest = {
				reason,
				instigator,
				actioned_by_travelnest: true,
				additional_info: additionalInfo,
			};
			await api.availabilityV3.createCancellationRequest(
				propertyId.toString(),
				eventId,
				cancellationRequest
			);

			// Add event note
			const username = (
				await api.authentication.getCurrentUser()
			).getUsername();
			const eventNote = {
				action: 'add',
				eventNote: {
					event_id: eventId,
					note_type: 'audit',
					content: bypassChecked
						? `${username} requested a host cancellation without cancellation fee payment`
						: `${username} requested a host cancellation payment`,
				} as IEventNote,
				eventId,
			} as IPendingEventNote;

			await submitEventNotes(propertyId.toString(), eventId, [eventNote]);

			if (!bypassChecked) {
				await api.availabilityV3.addHostCancellationPostPayment(
					propertyId.toString(),
					eventId
				);

				// We try and charge for payment, but if it fails we just log the error and continue
				try {
					const paymentDetails =
						await api.hostPayments.searchPaymentDetails(customerId);
					if (
						paymentDetails &&
						Object.keys(paymentDetails).length > 0
					) {
						const payment =
							await api.availabilityV3.getHostCancellationPayment(
								propertyId.toString(),
								eventId
							);

						await api.hostPayments.chargeHostCancellation(
							customerId,
							eventId,
							payment.price,
							payment.currency,
							payment.description,
							payment.tax
						);
					}
				} catch (error) {
					console.error(error);
				}
			}

			dispatch({
				type: 'CANCELLATION_REQUEST_RESPONSE',
				payload: { propertyId, eventId, cancellationRequest },
			});
			setSubmitting(false);
			onRequestClose();
		} catch (e) {
			setErrorSubmitting(e?.data?.body?.error || JSON.stringify(e));
			setSubmitting(false);
		}
	};

	return (
		<Modal
			title="Cancellation request"
			shouldCloseOnOverlayClick={false}
			onRequestClose={onRequestClose}
			actionRHS={
				<Button
					id="booking_cancellation_request_save"
					onClick={onSave}
					bgColor={theme.colours.success.default}
					color={theme.colours.text.light}
					disabled={!reason || !instigator}
					loading={submitting}
				>
					Save
				</Button>
			}
		>
			<div className="request">
				{errorSubmitting && (
					<Panel type="error">{errorSubmitting}</Panel>
				)}
				<Paragraph>
					Submit a cancellation request for this booking.
				</Paragraph>
				<Spacer />
				<Select
					placeholder="Select instigator"
					label="Instigator"
					id="instigator"
					value={instigator}
					onChange={(e) => setInstigator(e.target.value)}
				>
					{[
						{ value: '', label: '' },
						{ value: 'owner', label: 'Owner' },
						{ value: 'travelnest', label: 'Travelnest' },
						{ value: 'channel', label: 'Channel' },
					].map(({ value, label }) => (
						<option value={value} key={value}>
							{label}
						</option>
					))}
				</Select>
				<Spacer />
				<Select
					placeholder="Select reason"
					label="Reason"
					id="cancellation-reason"
					value={reason}
					onChange={(e) => setReason(e.target.value)}
				>
					{[
						{ value: '', label: '' },
						{ value: 'emergency', label: 'Emergency' },
						{ value: 'unavailable', label: 'Unavailable' },
						{ value: 'double_booking', label: 'Double booking' },
						{ value: 'amendment', label: 'Amendment' },
						{ value: 'guest_request', label: 'Guest request' },
						{ value: 'churn', label: 'Churn' },
						{ value: 'other', label: 'Other' },
					].map(({ value, label }) => (
						<option value={value} key={value}>
							{label}
						</option>
					))}
				</Select>
				<Spacer />

				<TextArea
					id="additional_info"
					onChange={(_, value) => setAdditionalInfo(value)}
					label="Additional info"
					valid={true}
					value={additionalInfo}
					style={{ height: '160px' }}
					maxLength={Infinity}
				/>
				<Spacer />
				<FormInput
					name={`bypass-payment`}
					type="checkbox"
					checked={bypassChecked}
					onChange={(e) => setBypassChecked(e.target.checked)}
				/>
				<FormLabel htmlFor="bypass-payment">
					Bypass host cancellation payment
				</FormLabel>
			</div>
		</Modal>
	);
};
